import type { TImportTitles } from "components/ImportData/import_data";
import { getColumnMatchScore, JARO_WINKLER_MATCH_THRESHOLD } from "helpers/columnMatchScore";

const ID_ALTERNATE_KEYS = ["id", "_id", "ID"];

const getRawKey = (formattedKey: string) => formattedKey.replace(/^_/, "");

const canReuseCrealoKey = (crealoKey: string, usedCrealoKeys: Set<string>, repeatedKeys: string[]) => {
    if (crealoKey === "Ignore") return true;
    if (repeatedKeys.includes(getRawKey(crealoKey))) return true;
    return !usedCrealoKeys.has(crealoKey);
};

const getMatchScore = (fileColumn: string, formattedKey: string, label: string, alternateKeys: string[] = []) => {
    const candidates = [
        formattedKey,
        getRawKey(formattedKey),
        label,
        ...alternateKeys,
        ...(formattedKey === "_id" ? ID_ALTERNATE_KEYS : []),
    ];

    return getColumnMatchScore(fileColumn, candidates);
};

const findExactMatch = (
    fileColumn: string,
    crealoKeys: TImportTitles,
    acceptedKeys: Record<string, string[]>,
): string | undefined => {
    for (const formattedKey of Object.keys(crealoKeys)) {
        const rawKey = getRawKey(formattedKey);
        const label = crealoKeys[formattedKey];
        const alternateKeys = acceptedKeys[rawKey] ?? [];
        const candidates = [
            formattedKey,
            rawKey,
            label,
            ...alternateKeys,
            ...(formattedKey === "_id" ? ID_ALTERNATE_KEYS : []),
        ];

        if (candidates.includes(fileColumn)) {
            return formattedKey;
        }
    }

    return undefined;
};

const findBestFuzzyMatch = (
    fileColumn: string,
    crealoKeys: TImportTitles,
    acceptedKeys: Record<string, string[]>,
    usedCrealoKeys: Set<string>,
    repeatedKeys: string[],
): string | undefined => {
    let bestMatch: string | undefined;
    let bestScore = 0;

    Object.keys(crealoKeys).forEach((formattedKey) => {
        if (!canReuseCrealoKey(formattedKey, usedCrealoKeys, repeatedKeys)) return;

        const rawKey = getRawKey(formattedKey);
        const score = getMatchScore(fileColumn, formattedKey, crealoKeys[formattedKey], acceptedKeys[rawKey]);

        if (score >= JARO_WINKLER_MATCH_THRESHOLD && score > bestScore) {
            bestScore = score;
            bestMatch = formattedKey;
        }
    });

    return bestMatch;
};

/**
 * Maps each file column to a Crealo field.
 * Saved choice, then exact accepted key, then Jaro-Winkler. Unmatched columns are "Ignore".
 */
export const suggestColumnMapping = ({
    fileTitles,
    crealoKeys,
    acceptedKeys,
    savedMapping,
    repeatedKeys = [],
}: {
    fileTitles: string[];
    crealoKeys: TImportTitles;
    acceptedKeys: Record<string, string[]>;
    savedMapping?: Record<string, string>;
    repeatedKeys?: string[];
}): Record<string, string> => {
    const mapping: Record<string, string> = {};
    const usedCrealoKeys = new Set<string>();
    const formattedKeys = Object.keys(crealoKeys);

    fileTitles.forEach((fileColumn) => {
        const savedValue = savedMapping?.[fileColumn];
        const exactMatch = findExactMatch(fileColumn, crealoKeys, acceptedKeys);
        // A remembered "Ignore" must not hide a column we now recognize exactly
        const ignoresExactMatch = savedValue === "Ignore" && !!exactMatch;
        if (
            savedValue &&
            !ignoresExactMatch &&
            (savedValue === "Ignore" || formattedKeys.includes(savedValue)) &&
            canReuseCrealoKey(savedValue, usedCrealoKeys, repeatedKeys)
        ) {
            mapping[fileColumn] = savedValue;
            if (savedValue !== "Ignore") usedCrealoKeys.add(savedValue);
            return;
        }

        if (exactMatch && canReuseCrealoKey(exactMatch, usedCrealoKeys, repeatedKeys)) {
            mapping[fileColumn] = exactMatch;
            usedCrealoKeys.add(exactMatch);
            return;
        }

        const fuzzyMatch = findBestFuzzyMatch(fileColumn, crealoKeys, acceptedKeys, usedCrealoKeys, repeatedKeys);
        mapping[fileColumn] = fuzzyMatch ?? "Ignore";
        if (fuzzyMatch) usedCrealoKeys.add(fuzzyMatch);
    });

    return mapping;
};
